import { useState, useMemo, useCallback } from "react";
import { Button, addToast } from "@heroui/react";
import { FolderIcon, ArrowRightIcon, FolderSimpleIcon } from "@phosphor-icons/react";
import { useShallow } from "zustand/shallow";
import { clsx } from "clsx";

import { useBoundStore } from "@/app/store";
import { FolderTreeNode } from "@/app/store/foldersSlice";
import { FetchedNote } from "@/services/api/fetchers/notes";
import { NestedContentNavigator, NestedAction } from "@/shared/components/ui/NestedContentNavigator";
import { ICON_SIZE } from "@/config/constants";
import { getFolderColorClassName } from "@/features/folders/utils/general";

interface MoveNoteOptionProps {
  note: FetchedNote;
  onClose?: () => void;
}

const ROOT_FOLDER_NAME = "Home";

export function MoveNoteOption({ note, onClose }: MoveNoteOptionProps) {
  const folders = useBoundStore(useShallow((state) => state.folders.main));
  const folderTree = useBoundStore(useShallow((state) => state.folders.tree));
  const updateNote = useBoundStore(useShallow((state) => state.notes.updateNote));
  const [isMoving, setIsMoving] = useState(false);
  const currentFolderId = note.folder_id;

  const handleMoveNote = useCallback(
    async (targetFolderId: number | null) => {
      if (targetFolderId === currentFolderId) {
        onClose?.();
        return;
      }

      setIsMoving(true);

      const targetFolderName = targetFolderId ? folders.find((f) => f.id === targetFolderId)?.name : ROOT_FOLDER_NAME;
      const isShared = Boolean(note.public_uuid);

      updateNote(note.id, { folder_id: targetFolderId }, isShared)
        .then(() =>
          addToast({
            title: `Note moved to ${targetFolderName}`,
          })
        )
        .catch((error) => {
          console.error("Failed to move note:", error);
          addToast({
            title: "Failed to move note",
            color: "danger",
          });
        });

      onClose?.();
      setIsMoving(false);
    },
    [currentFolderId, onClose, updateNote, note, folders]
  );

  // Build folder actions for any given set of folders
  const buildFolderActions = useCallback(
    (folderNodes: FolderTreeNode[]): NestedAction<string>[] => {
      return folderNodes.map((folder) => {
        const hasChildren = folder.children.length > 0;
        const isCurrentFolder = currentFolderId === folder.id;

        if (hasChildren) {
          // For folders with children, create a nested structure with "Move here" and children
          return {
            key: folder.id.toString(),
            label: folder.name,
            icon: (
              <FolderSimpleIcon
                className={clsx("shrink-0", getFolderColorClassName(folder.color))}
                size={ICON_SIZE.md}
                weight="duotone"
              />
            ),
            nestedActions: () => [
              // "Move here" action for the folder itself, disabled if current
              {
                key: `${folder.id}-move`,
                label: `Move to "${folder.name}"`,
                icon: (
                  <FolderSimpleIcon
                    className={clsx("shrink-0", getFolderColorClassName(folder.color))}
                    size={18}
                    weight="duotone"
                  />
                ),
                run: isCurrentFolder ? undefined : () => handleMoveNote(folder.id),
              },
              // Children folders
              ...buildFolderActions(folder.children),
            ],
          };
        } else {
          // For folders without children, show as direct action
          return {
            key: folder.id.toString(),
            label: folder.name,
            icon: (
              <FolderSimpleIcon
                className={clsx("shrink-0", getFolderColorClassName(folder.color))}
                size={18}
                weight="duotone"
              />
            ),
            run: isCurrentFolder ? undefined : () => handleMoveNote(folder.id),
          };
        }
      });
    },
    [currentFolderId, handleMoveNote]
  );

  // Root level actions
  const rootActions: NestedAction<string>[] = useMemo(() => {
    const actions: NestedAction<string>[] = [];

    // Add "Move to Root" option
    actions.push({
      key: "root",
      label: ROOT_FOLDER_NAME,
      icon: <FolderIcon size={18} weight={currentFolderId === null ? "fill" : "regular"} />,
      run: currentFolderId === null ? undefined : () => handleMoveNote(null),
    });

    // Add all root folders
    const folderActions = buildFolderActions(folderTree);
    actions.push(...folderActions);

    return actions;
  }, [folderTree, currentFolderId, handleMoveNote, buildFolderActions]);

  if (folders.length === 0) {
    return <div className="text-small text-default-500 p-4 text-center">No folders available</div>;
  }

  return (
    <div className="w-full max-w-sm p-1">
      <div className="mb-3">
        <h3 className="text-md font-semibold">Move Note to Folder</h3>
        <p className="text-small text-default-500">Select a destination folder</p>
      </div>

      <NestedContentNavigator
        actions={rootActions}
        renderAction={(action, onPress) => {
          // For folders with nested actions, they should be navigable even without a run function
          const isCurrent = action.run === undefined && !action.nestedActions;
          const isDisabled = isMoving || isCurrent;

          return (
            <Button
              endContent={
                action.nestedActions ? (
                  <ArrowRightIcon size={16} />
                ) : isCurrent ? (
                  <span className="text-success text-xs">Current</span>
                ) : null
              }
              fullWidth
              isDisabled={isDisabled}
              key={action.key}
              startContent={action.icon}
              variant="light"
              onPress={onPress}
            >
              <span className="flex-1 overflow-x-hidden text-left text-ellipsis">{action.label}</span>
            </Button>
          );
        }}
      />
    </div>
  );
}
